//CoverLetter.jsx (CoverLetter Add/Update/Delete)

import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import 'react-datepicker/dist/react-datepicker.css';
import QRCode from 'react-qr-code';
import '../../styles/Concerns.css';
import '../../styles/Alert.css';
import { GenerateCoverLetterCode } from '../../components/GenerateThink5whysCode';

const CoverLetter = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const { coverletterId } = useParams();
  const mode = coverletterId === undefined ? "add" : "update";
  const [coverLetter, setCoverLetter] = useState(null);
  const [userId, setUserId] = useState(authState.user._id);

  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [countFormErrors, setCountFormErrors] = useState(0);

  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState(null);
  const [autoCoverLetterCode, setAutoCoverLetterCode] = useState(GenerateCoverLetterCode());

  const [formData, setFormData] = useState({
    user: userId,
    userCode: userCode,
    userName: userName,
    coverLetterCode: autoCoverLetterCode,
    resumeCode: "",
    jdCode: "",
    title: "",
    coverLetterSummary: "",
    remarks: "",
    dateApplied: null,
    shortlistStatus: "",
    shortlistRemarks: "",
    shortlistDate: null,
    isActive: true,
    isDeleted: false,
    dateCreated: new Date(),
    createdBy: authState.user._id,
  });
  
  useEffect(() => {
    document.title = mode === "add" ? "Add" : "Update";
  }, [mode]);

  useEffect(() => {
    if (mode === "update") {
      const config = { 
        url: `/coverLetter/getforupdate`, 
        method: "post", 
        headers: { Authorization: authState.token }, 
        data: { coverLetterId: coverletterId }
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((data) => {
          setCoverLetter(data.coverLetter);
          setFormData({
            userCode: data.coverLetter.userCode,
            userName: data.coverLetter.userName,
            coverLetterCode: data.coverLetter.coverLetterCode,
            resumeCode: data.coverLetter.resumeCode,
            jdCode: data.coverLetter.jdCode,
            title: data.coverLetter.title,
            coverLetterSummary: data.coverLetter.coverLetterSummary,
            remarks: data.coverLetter.remarks,
            dateApplied: data.coverLetter.dateApplied,
            shortlistStatus: data.coverLetter.shortlistStatus,
            shortlistRemarks: data.coverLetter.shortlistRemarks,
            shortlistDate: data.coverLetter.shortlistDate,
            isActive: data.coverLetter.isActive,
            isDeleted: data.coverLetter.isDeleted,
            dateCreated: data.coverLetter.dateCreated,
            createdBy: data.coverLetter.createdBy,
          });
        })
        .catch((error) => {
          console.error("Error fetching CoverLetter data:", error);
          setError("Error fetching CoverLetter data");
        });
    }
  }, [mode, authState, coverletterId, fetchData, setCoverLetter]);
  

  const handleChange = (e) => {
    setFormData({
      ...formData, [e.target.name]: e.target.value
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    const error = validateForm(formData);
    setFormErrors(error);
    setCountFormErrors(Object.keys(error).length);
    if (Object.keys(error).length > 0) {
      setFormErrors(
        Object.keys(error).reduce((total, key) => {
          total[key] = error[key];
          return total;
        }, {})
      );
      return;
    }

    if (mode === "add") {
      const config = { 
        url: "/coverLetter", 
        method: "post",
        data: {formData: formData, user: authState.user._id}, 
        headers: { Authorization: authState.token } 
      };
      fetchData(config).then(() => {
        navigate("/coverLetter-list");
      });
    }
    else {
      const config = { 
        url: `/coverLetter/${coverletterId}`, 
        method: "put", 
        data: formData, 
        headers: { Authorization: authState.token } 
      };
      fetchData(config).then(() => {
        navigate("/coverLetter-list");
      })
      .catch((error) => {
        console.error("Error updating CoverLetter:", error);
        setError("Error updating CoverLetter");
      });
    }
  }

  const validateForm = (formData = {}) => {
    const errors = {};
    if (!formData.title) {
      errors.title = "Title is required";
    } else if (formData.title.length > 100) {
      errors.title = "Title cannot exceed 100 characters";
    }
    if (!formData.coverLetterSummary) {
      errors.coverLetterSummary = "CoverLetter Summary is required";
    } else if (formData.coverLetterSummary.length > 10000) {
      errors.coverLetterSummary = "CoverLetter Summary cannot exceed 10000 characters";
    }
    return errors;
  }

  const fieldError = (field) => (
    <p className={`mt-1 text-pink-600 text-sm ${formErrors[field] ? "block" : "hidden"}`}>
      <i className='mr-2 fa-solid fa-circle-exclamation'></i>
      {formErrors[field]}
    </p>
  )

  return (
    <>
      <MainLayout>
      <div className="min-h-full py-8 bg-gradient-to-r from-blue-700 via-blue-700 to-blue-700 mt-12 rounded-lg">
        <h2 className='text-2xl font-bold text-white text-center mb-2'>My CoverLetter</h2>
            {loading ? (
              <Loader />
            ) : (
            <div className='text-sm'>
              <div className='flex justify-center'>
                <h2 className="ml-2 text-xl text-yellow-200">{mode === "add" ? "Add New CoverLetter" : "Edit CoverLetter"}</h2>
              </div>
              <div className="border-container text-m ">
                  <div className="p-2 bg-white shadow-md rounded-lg mb-4 flex flex-row justify-between">
                    <p className="text-gray-700 text-m font-semibold">Your CoverLetter Code: {autoCoverLetterCode}</p>
                    <QRCode value={autoCoverLetterCode} size={64} className="ml-4" />
                  </div>
              </div>
              <div className="border-container-gray ml-8">
                <div className="mb-2 text-sm">
                  <label htmlFor="title">CoverLetter Title*:</label>
                  <br />
                  <input 
                    type="text" 
                    name="title" 
                    id="title" 
                    value={formData.title} 
                    placeholder="Enter the title..."
                    maxLength="100"
                    onChange={handleChange}
                    style={{
                      border: '1px solid #ccc',
                      padding: '8px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }} 
                  />
                  {fieldError("title")}
                </div>
              </div>
              <div className="border-container">
                <div className="mb-2 text-sm text-white">
                    <p>Description*:</p>
                      <textarea 
                        type="coverLetterSummary" 
                        name="coverLetterSummary" 
                        id="coverLetterSummary" 
                        value={formData.coverLetterSummary}
                        onChange={handleChange}
                        maxLength="10000"
                        style={{
                          border: '1px solid #ccc',
                          padding: '2px',
                          backgroundColor: '#f0f0f0', 
                          color: '#000000',
                          maxWidth: '100%', 
                          maxHeight: '150px' 
                        }}
                      />
                    {fieldError("coverLetterSummary")}
                </div>
                <div className="mb-2 text-sm">
                  <label htmlFor="resumeCode">Resume Code:</label>
                  <br />
                  <input type="text" name="resumeCode" id="resumeCode" value={formData.resumeCode} placeholder="Enter Resume Code..."
                    onChange={handleChange} style={{ border: '1px solid #ccc', padding: '8px', width: '150px' }} />
                  {fieldError("resumeCode")}
                </div>
                <div className="mb-2 text-sm">
                  <label htmlFor="jdCode">Job Code:</label>
                  <br />
                  <input type="text" name="jdCode" id="jdCode" value={formData.jdCode} placeholder="Enter Job Code..."
                    onChange={handleChange} style={{ border: '1px solid #ccc', padding: '8px', width: '150px' }} />
                  {fieldError("jdCode")}
                </div>
                <div className="mb-2 text-sm">
                  <label htmlFor="remarks">Remarks:</label>
                  <br />
                  <input type="text" name="remarks" id="remarks" value={formData.remarks} placeholder="Enter remarks..."
                    onChange={handleChange} style={{ border: '1px solid #ccc', padding: '8px', width: '100%' }} />
                  {fieldError("remarks")}
                </div>
                <div className="mb-2 text-sm text-white">
                  <input 
                    type="checkbox" 
                    className="mr-2 cursor-pointer "
                    checked={formData.isActive} 
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                  />
                  <span className="text-sm font-medium text-white">Active</span>
                </div>
              </div>
              <div className="border-container flex flex-col justify-center">
                <div className="flex justify-center">
                  {Object.keys(formErrors).length > 0 && (
                    <div className="form-errors text-red-300 justify-center items-center">
                      {Object.keys(formErrors).map((key) => (
                        <div className="flex justify-center items-center" key={key}>{formErrors[key]}</div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-center">
                  <button 
                    className='bg-primary text-white px-4 py-2 font-medium hover:bg-primary-dark' 
                    onClick={handleSubmit}>
                      {mode === "add" ? "Add New" : "Update"}
                  </button>
                  <button 
                    className='ml-4 bg-red-500 text-white px-4 py-2 font-medium' 
                    onClick={() => navigate("/life-chronicles-home")}>
                      Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </MainLayout>
    </>
  )
}

export default CoverLetter;